/**
 * 今天复习什么，以及按条件抽一条知识。这是 library + states + settings 上的纯查询，不是会话对象。
 * 四种复习形式（到期、抽样、浏览、预览）都从这一个函数出，到期判定只有一套，没有第二条复习路径。
 * 用 model、scheduling、library 与 ports 的种子随机源，被 session 与屏幕调用。
 * 不推进 CardState，不记账，也没有地方能持有队列副本，所以重启与刷新都得到同一个队列。
 */

import { scoringOf } from './question-types'
import type { Scoring } from './question-types'
import type { CardKey, CardRef, ConceptId, EpochDay, PlanId } from '../model/ids'
import { cardKey, conceptCard, parseCardKey, planId, questionCard } from '../model/ids'
import type { Concept, Difficulty, RecallCard, Tag } from '../model/concept'
import { recallCardOf } from '../model/concept'
import type { AnyQuestion } from '../model/question'
import type { CardState, States } from './scheduling'
import { dueOf, overdueDays, stateFor } from './scheduling'
import type { Library } from '../content/library'
import type { Settings } from '../ports'
import { seededRng } from '../ports'

/** 这张卡为什么出现在这一轮里。 */
export type QueueReason = 'due' | 'new' | 'sampled' | 'listed'

/** 知识点卡的作答对象是条目本身，所以它带的是派生出来的复述要点，不是一道 Question。 */
export interface ConceptSubject {
	readonly kind: 'concept'
	readonly concept: Concept
	readonly recall: RecallCard
}

export interface QuestionSubject {
	readonly kind: 'question'
	readonly question: AnyQuestion
	/** 题卡也带所属条目：AnyQuestion 只有 conceptId，没有它 conceptOf 对题卡就成了偏函数。 */
	readonly concept: Concept
}

export type QueueSubject = ConceptSubject | QuestionSubject

export interface QueueItem {
	readonly reason: QueueReason
	readonly subject: QueueSubject
	/** null 就是新卡：状态表里没有它的键。这是「没复习过」的唯一表示。 */
	readonly state: CardState | null
	/** 新卡没有到期日。 */
	readonly dueOn: EpochDay | null
	readonly overdue: number
}

/** 卡片身份从 subject 派生，所以队列里不存在第二份可能和 subject 不一致的 ref 字段。 */
export function cardOf(item: QueueItem): CardRef {
	return item.subject.kind === 'concept' ? conceptCard(item.subject.concept.id) : questionCard(item.subject.question.id)
}

export function conceptOf(item: QueueItem): Concept {
	return item.subject.concept
}

export function conceptIdOf(item: QueueItem): ConceptId {
	return conceptOf(item).id
}

/** 自评还是自动判分，队列层给结论：知识点卡恒为 self，题卡问 descriptor。会话机据此选相变。 */
export function itemScoring(item: QueueItem): Scoring {
	return item.subject.kind === 'concept' ? 'self' : scoringOf(item.subject.question.kind)
}

/** 抽样的过滤条件。难度区间两端都含。 */
export interface DifficultyRange {
	readonly min: Difficulty
	readonly max: Difficulty
}

export interface PickCriteria {
	readonly tag?: Tag
	readonly difficulty?: DifficultyRange
	/** true 时只在没有复习记录的条目里抽，用于「开新知识点」。 */
	readonly unseenOnly?: boolean
}

export type PlanIntent =
	| { readonly kind: 'due'; readonly limit: number }
	| { readonly kind: 'sample'; readonly size: number; readonly seed: number; readonly criteria: PickCriteria }
	/** 用户点开某个条目的练习，或巩固形式自己攒的一批卡。 */
	| { readonly kind: 'browse'; readonly cards: readonly CardRef[] }
	/** 只读轮：相变照常跑，但不产生 Review 记录，「看一眼」不会污染进度。 */
	| { readonly kind: 'preview'; readonly cards: readonly CardRef[] }

export type PlanNoteCode = 'emptyPool' | 'cardGone' | 'newQuotaReached' | 'poolSize'

/** 排期过程中的降级说明。抽到不存在的卡、池子空了，都必须让用户看见，不能静默少一张。 */
export interface PlanNote {
	readonly code: PlanNoteCode
	readonly detail: string
}

export interface Plan {
	/**
	 * submissionId 的前缀。由 (当天, intent, 卡片键集合) 算出。
	 * 集合参与哈希是有意的：一天内连续答几张后重启，队列会缩，新 planId 也随之变化，
	 * 于是新一份 plan 的 cursor 0/1/2... 不会撞上旧 planId 下同 cursor 已写过的 submissionId，
	 * 被 seenSubmissions 静默当成重放。跨重启的真重放（同日同集合）仍走 seenSubmissions 幂等。
	 */
	readonly planId: PlanId
	readonly intent: PlanIntent
	readonly generatedOn: EpochDay
	/** 到期卡在前，逾期多的在前，同分按卡片键定序，所以两次构建结果完全一致。 */
	readonly items: readonly QueueItem[]
	/** 今天到期但没排进队列的数量。limit 造成的积压必须可见。 */
	readonly carryOver: number
	readonly notes: readonly PlanNote[]
}

export interface PlanInput {
	readonly library: Library
	readonly states: States
	readonly settings: Settings
}

/**
 * 唯一的队列构建点。随机性只来自 intent.sample 的 seed，所以本函数是纯函数，
 * 同一份输入两次调用给出完全相同的 Plan。
 * due     → states 里 dueOf <= today 且 lastReviewedOn !== today 的卡，加前 newPerDay 张新题卡
 * sample  → 按 criteria 过滤条目，用 seededRng(seed) 选 size 个，各出一张知识点卡
 * browse  → 直接映射卡片键，仓库里已删掉的记 cardGone
 * preview → 与 browse 同，只有 intent 标签不同，记账由会话机挡住
 * 排序键 (dueOn 升, overdue 降, lapses 降, cardKey 升)。今天已答过的卡由 lastReviewedOn === today 排除，
 * again 的当天重看交给 UI 的本轮队尾，因为当天重看不该再推进间隔。
 */
export function buildPlan(input: PlanInput, intent: PlanIntent, now: EpochDay): Plan {
	const draft =
		intent.kind === 'due'
			? dueDraft(input, intent.limit, now)
			: intent.kind === 'sample'
				? sampleDraft(input, intent, now)
				: listedDraft(input, intent.cards, now)
	return { planId: makePlanId(now, intent, draft.items), intent, generatedOn: now, ...draft }
}

/** planId 是 submissionId 的前缀，所以它必须由 (当天, intent, 卡片键集合) 算出，不能是随机数。 */
export function makePlanId(now: EpochDay, intent: PlanIntent, items: readonly QueueItem[]): PlanId {
	const keys = [...new Set(items.map((item) => cardKey(cardOf(item))))].sort(compareKeys)
	return planId(`${intent.kind}-${now}-${fnv1a(`${now}|${serializeIntent(intent)}|${keys.join(',')}`)}`)
}

/** 每个 intent 各产出一份 Draft，buildPlan 只负责给它加上 planId 与 generatedOn。 */
interface Draft {
	readonly items: QueueItem[]
	readonly notes: PlanNote[]
	readonly carryOver: number
}

type SampleIntent = Extract<PlanIntent, { readonly kind: 'sample' }>

/** 新卡没有到期日，排序时当成最远的一天，于是每天都先还旧债。 */
const NO_DUE_ON = Number.MAX_SAFE_INTEGER

function dueDraft(input: PlanInput, limit: number, now: EpochDay): Draft {
	const { library, states, settings } = input
	const notes: PlanNote[] = []
	const due: QueueItem[] = []
	for (const [key, state] of states) {
		if (state.lastReviewedOn === now || dueOf(state) > now) continue
		const ref = parseCardKey(key)
		const item = ref === null ? null : toItem(library, ref, state, 'due', now)
		if (item === null) notes.push(cardGone(key))
		else due.push(item)
	}
	const fresh: QueueItem[] = []
	let deferred = 0
	for (const concept of [...library.concepts].sort(newCardOrder)) {
		// 新卡预算只给题卡。知识点卡要用户主动抽或浏览才引入，否则每天的队列会被自动塞进几十张自评卡。
		for (const question of concept.questions) {
			if (states.has(cardKey(questionCard(question.id)))) continue
			if (fresh.length >= settings.newPerDay) deferred += 1
			else fresh.push(itemOf({ kind: 'question', question, concept }, null, 'new', now))
		}
	}
	const queue = [...due, ...fresh].sort(compareItems)
	const items = queue.slice(0, Math.max(0, limit))
	if (deferred > 0) notes.push({ code: 'newQuotaReached', detail: `今日新卡额度 ${settings.newPerDay} 张，另有 ${deferred} 张待引入` })
	const queuedDue = items.reduce((n, item) => (item.reason === 'due' ? n + 1 : n), 0)
	return { items, notes, carryOver: due.length - queuedDue }
}

function sampleDraft(input: PlanInput, intent: SampleIntent, now: EpochDay): Draft {
	const pool = samplePool(input, now, intent.criteria)
	const notes: PlanNote[] = []
	if (pool.length === 0) notes.push({ code: 'emptyPool', detail: `没有符合条件的知识点（${describeCriteria(intent.criteria)}）` })
	else if (intent.size > pool.length) notes.push({ code: 'poolSize', detail: `池子 ${pool.length} 条，少于请求的 ${intent.size} 条` })
	const rng = seededRng(intent.seed)
	const take = Math.min(Math.max(0, intent.size), pool.length)
	const picked: Concept[] = []
	const bag = [...pool]
	// 不放回抽样：每轮从剩下的袋子里抽走一张，所以取几个就消耗几个 rng 步，结果只由池子顺序和 seed 决定。
	for (let i = 0; i < take; i += 1) {
		const [chosen] = bag.splice(rng.nextInt(bag.length), 1)
		if (chosen !== undefined) picked.push(chosen)
	}
	const items = picked.map((concept) => itemOf(conceptSubject(concept), stateFor(input.states, conceptCard(concept.id)) ?? null, 'sampled', now))
	return { items: items.sort(compareItems), notes, carryOver: 0 }
}

function listedDraft(input: PlanInput, cards: readonly CardRef[], now: EpochDay): Draft {
	const notes: PlanNote[] = []
	const items: QueueItem[] = []
	const seen = new Set<CardKey>()
	for (const ref of cards) {
		const key = cardKey(ref)
		// 重复键只留第一次：同一张卡在一轮里被 advance 两次，间隔就凭空多扩了一档。
		if (seen.has(key)) continue
		seen.add(key)
		const item = toItem(input.library, ref, input.states.get(key) ?? null, 'listed', now)
		if (item === null) notes.push(cardGone(key))
		else items.push(item)
	}
	return { items: items.sort(compareItems), notes, carryOver: 0 }
}

/** 抽样池。今天已自评过的条目不再出现，因为「再抽一次今天刚读过的东西」不是用户要的那一下。 */
function samplePool(input: PlanInput, now: EpochDay, criteria: PickCriteria): readonly Concept[] {
	const candidates = criteria.tag === undefined ? input.library.concepts : input.library.byTag(criteria.tag)
	const range = criteria.difficulty
	const pool = candidates.filter((concept) => {
		if (range !== undefined && (concept.difficulty < range.min || concept.difficulty > range.max)) return false
		if (stateFor(input.states, conceptCard(concept.id))?.lastReviewedOn === now) return false
		return criteria.unseenOnly !== true || unseenConcept(input.states, concept)
	})
	return pool.sort((a, b) => compareKeys(cardKey(conceptCard(a.id)), cardKey(conceptCard(b.id))))
}

/** 「没碰过」看整个条目：只看知识点卡的话，练过它下面题目的条目会冒充新知识点。 */
function unseenConcept(states: States, concept: Concept): boolean {
	const keys = [cardKey(conceptCard(concept.id)), ...concept.questions.map((question) => cardKey(questionCard(question.id)))]
	return keys.every((key) => !states.has(key))
}

/** 卡片键 → 队列项。仓库里查不到这张卡时返回 null，由调用方记 cardGone，不用半张卡凑一个队列项。 */
function toItem(library: Library, ref: CardRef, state: CardState | null, reason: QueueReason, now: EpochDay): QueueItem | null {
	if (ref.kind === 'concept') {
		const concept = library.concept(ref.conceptId)
		return concept === undefined ? null : itemOf(conceptSubject(concept), state, reason, now)
	}
	const question = library.question(ref.questionId)
	const concept = question === undefined ? undefined : library.concept(question.conceptId)
	if (question === undefined || concept === undefined) return null
	return itemOf({ kind: 'question', question, concept }, state, reason, now)
}

function conceptSubject(concept: Concept): ConceptSubject {
	return { kind: 'concept', concept, recall: recallCardOf(concept) }
}

/** 队列项的唯一构造点。state、dueOn、overdue 三件事在这里一次算定，所以不存在「状态是旧的、到期日是新的」。 */
function itemOf(subject: QueueSubject, state: CardState | null, reason: QueueReason, now: EpochDay): QueueItem {
	return {
		reason,
		subject,
		state,
		dueOn: state === null ? null : dueOf(state),
		overdue: state === null ? 0 : overdueDays(state, now),
	}
}

/** 排序键 (dueOn 升, overdue 降, lapses 降, cardKey 升)，最后一条保证完全同分的两张卡也有唯一次序。 */
function compareItems(a: QueueItem, b: QueueItem): number {
	const byDue = (a.dueOn ?? NO_DUE_ON) - (b.dueOn ?? NO_DUE_ON)
	if (byDue !== 0) return byDue
	if (a.overdue !== b.overdue) return b.overdue - a.overdue
	const lapses = (b.state?.lapses ?? 0) - (a.state?.lapses ?? 0)
	if (lapses !== 0) return lapses
	return compareKeys(cardKey(cardOf(a)), cardKey(cardOf(b)))
}

/** 新卡引入顺序：作者标的难度低的在前，同难度按文件路径，再按文件里的书写顺序。 */
function newCardOrder(a: Concept, b: Concept): number {
	return a.difficulty !== b.difficulty ? a.difficulty - b.difficulty : compareKeys(a.path, b.path)
}

/** 不用 localeCompare：它的次序随区域设置变，而「两次构建结果完全一致」是队列对调用方的承诺。 */
function compareKeys(a: string, b: string): number {
	return a < b ? -1 : a > b ? 1 : 0
}

function cardGone(key: CardKey): PlanNote {
	return { code: 'cardGone', detail: `仓库里已经没有这张卡：${key}` }
}

function describeCriteria(criteria: PickCriteria): string {
	const parts: string[] = []
	if (criteria.tag !== undefined) parts.push(`标签 ${criteria.tag}`)
	if (criteria.difficulty !== undefined) parts.push(`难度 ${criteria.difficulty.min} 到 ${criteria.difficulty.max}`)
	if (criteria.unseenOnly === true) parts.push('仅未复习过')
	return parts.length === 0 ? '无筛选条件' : parts.join('，')
}

/** intent 的稳定序列化：字段顺序写死在这里，调用方写对象的顺序进不了身份。 */
function serializeIntent(intent: PlanIntent): string {
	switch (intent.kind) {
		case 'due':
			return `due:${intent.limit}`
		case 'sample':
			return `sample:${intent.size}:${intent.seed}:${serializeCriteria(intent.criteria)}`
		case 'browse':
		case 'preview':
			return `${intent.kind}:${intent.cards.map((card) => cardKey(card)).join(',')}`
	}
}

function serializeCriteria(criteria: PickCriteria): string {
	const parts: string[] = []
	if (criteria.tag !== undefined) parts.push(`tag=${criteria.tag}`)
	if (criteria.difficulty !== undefined) parts.push(`d=${criteria.difficulty.min}-${criteria.difficulty.max}`)
	if (criteria.unseenOnly !== undefined) parts.push(`unseen=${criteria.unseenOnly ? 1 : 0}`)
	return parts.join('&')
}

/** FNV-1a 32 位。planId 只要区分「同一天、同 intent、同卡集」，不需要密码学强度。 */
function fnv1a(text: string): string {
	let hash = 0x811c9dc5
	for (let i = 0; i < text.length; i += 1) hash = Math.imul(hash ^ text.charCodeAt(i), 0x01000193) >>> 0
	return hash.toString(36)
}
